type BottomControlOffsetInput = {
    panelBottom: number
    navigationTop: number
    visibleViewportBottom: number
    gap: number
}

export function getBottomControlOffset({
    panelBottom,
    navigationTop,
    visibleViewportBottom,
    gap,
}: BottomControlOffsetInput) {
    const obstructionTop = Math.min(navigationTop, visibleViewportBottom)
    return Math.max(gap, Math.ceil(panelBottom - obstructionTop + gap))
}

// Where the navbar's top edge stands, with any transform it is wearing taken
// back out: its return from the keyboard (control.module.scss
// mobileControlReturn) starts it translated below the edge, and the foot that
// stands on it is measured in the very render that starts that return.
// Measured from where the animation has it, the clearance came out zero and
// the composer pill settled overlapping the navbar. A translation is the only
// transform the navbar wears; anything else is left as measured.
export function resolveNavigationTop({
    rectTop,
    transform,
}: {
    rectTop: number
    transform: string | null | undefined
}): number {
    const translateY = readTransformTranslateY(transform)
    if (translateY == null) return rectTop
    // The matrix string carries a few decimals of the translation, so the
    // difference can miss the resting edge by a hair, and the clearance built
    // on it rounds up a whole pixel: settle it to the hundredth.
    return Math.round((rectTop - translateY) * 100) / 100
}

// The vertical translation a computed transform carries, or null for none
// (or for anything that is not a plain 2D matrix).
export function readTransformTranslateY(transform: string | null | undefined): number | null {
    if (!transform || transform === 'none') return null
    // matrix(a, b, c, d, tx, ty) — the 2D form a translate resolves to.
    const match = /^matrix\(([^)]+)\)$/.exec(transform.trim())
    if (!match) return null
    const parts = match[1].split(',').map(part => Number(part.trim()))
    if (parts.length !== 6 || parts.some(part => !Number.isFinite(part))) return null
    return parts[5]
}

// The vertical part of a computed CSS `translate` property ("none", "Xpx" or
// "Xpx Ypx"), or 0. The bottom controls container slides with it when the
// phone chrome hides on scroll; rest-position measurements take it out.
export function readTranslatePropertyY(translate: string | null | undefined): number {
    if (!translate || translate === 'none') return 0
    const parts = translate.trim().split(/\s+/)
    const y = parts.length > 1 ? Number.parseFloat(parts[1]) : 0
    return Number.isFinite(y) ? y : 0
}

// The bottom controls container's current slide. Its translate is a calc()
// on its own height (panel-controller.module.scss), which the computed style
// leaves unresolved ("0px calc(100% + 80px)"), so it is read from geometry:
// the container is fixed, and a fixed box's offsetTop is its place in the
// viewport with every transform and translate left out. Its own transform is
// horizontal only, so the vertical difference is the slide alone.
export function bottomControlsSlideY(element: Element | null | undefined): number {
    const container = element?.closest?.('[data-bottom-controls]')
    if (!(container instanceof HTMLElement) || typeof getComputedStyle === 'undefined') return 0
    if (container.offsetParent === null && getComputedStyle(container).position === 'fixed') {
        return container.getBoundingClientRect().top - container.offsetTop
    }
    return readTranslatePropertyY(getComputedStyle(container).translate)
}
